<template lang="html">
  <div class="catalog-showcase relative">
    <div
      key="header"
      class="catalog-showcase__header mb-2 flex items-center justify-between border border-neutral-400 p-1"
    >
      <UCheckbox
        v-model="outOfStockModel"
        label="Скрыть отсутствующие"
        class="ml-2 text-sm"
      />

      <CatalogSortSelector v-model="currentSort" class="ml-auto" />
    </div>

    <div
      v-if="items.length"
      key="list"
      class="catalog-showcase__list grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4 md:grid-cols-[repeat(auto-fill,minmax(300px,1fr))]"
    >
      <ProductCard
        v-for="(product, index) in items"
        :key="product.id"
        :value="product"
        :category="category"
        :eager="index < EAGER_CARDS"
      />
    </div>

    <NonePanel v-if="!items.length && !pending" />

    <div ref="sentinelRef" class="catalog-showcase__sentinel absolute right-0 bottom-0 left-0 h-1" />

    <div
      v-if="pending"
      class="catalog-showcase__loader flex justify-center py-6"
    >
      <UIcon
        name="i-lucide-loader-circle"
        class="animate-spin text-2xl text-neutral-400"
      />
    </div>

    <!-- Краулябельная пагинация: обычные <a> (не NuxtLink — переход должен
    перезагрузить листинг с курсором из URL), краулер обходит каталог по
    цепочке rel=next; пользователям контент догружает бесконечный скролл -->
    <nav
      v-if="nextHref || pagination.isPaginated.value"
      class="catalog-showcase__pagination flex items-center justify-center gap-6 py-4"
    >
      <a
        v-if="pagination.isPaginated.value"
        :href="route.path"
        class="text-sm text-muted underline-offset-4 hover:underline"
      >← В начало каталога</a>
      <a
        v-if="nextHref"
        :href="nextHref"
        rel="next"
        class="text-sm text-muted underline-offset-4 hover:underline"
      >Следующая страница →</a>
    </nav>
  </div>
</template>

<script setup lang="ts">
import {
  ProductListItemDto,
  type EntityRef,
  type ProductsListGetRequestQuery,
} from '@repo/api/web/models'
import { useIntersectionObserver } from '@vueuse/core'

import CatalogSortSelector from './CatalogSortSelector.vue'

import { useEcommerce } from '#Analytics/composables/useEcommerce'
import { mapCatalogProduct } from '#Analytics/utils/ecommerceMapper'
import { SortDirection } from '#Common/models'
import ProductCard from '#Products/components/Card/ProductCard.vue'
import NonePanel from '~/app/components/None/NonePanel.vue'

const props = withDefaults(
  defineProps<{
    category?: EntityRef
    searchQuery?: string
  }>(),
  {},
)

const { service } = useProducts()

// Первый ряд сетки (LCP-кандидаты) грузится сразу, остальное — lazy
const EAGER_CARDS = 4

// Краулябельная пагинация: слайс листинга из URL (?page&afterField&afterId),
// «дальше» — видимая <a>-ссылка ниже; см. useListingPagination
const pagination = useListingPagination()
const route = useRoute()
const router = useRouter()

const { collection, pending, setFilter, load, hasAfter, nextPage, query, meta } = await useCursorListing<
  any,
  ProductListItemDto
>({
  key: 'CatalogShowcase' + props.category?.id,
  fetcher: (queryParams: ProductsListGetRequestQuery) =>
    service.list(queryParams),
  transform: data => ProductListItemDto.createFromCollection(data ?? []),
  initialQuery: {
    Limit: 24,
    Query: props.searchQuery,
    'Filter.OutOfStock': true,
    'Filter.CategoryId': props.category?.id,
    SortField: 'manual',
    SortDirection: SortDirection.ASC,
    ...(pagination.cursorQuery.value ?? {}),
  },
})

const nextHref = computed(() => pagination.nextPageHref(meta.value))

/**
 * Смена сортировки/фильтра/категории перезапрашивает листинг с начала —
 * курсор из URL при этом устаревает (описывает слайс старой выдачи):
 * чистим и параметры пагинации в адресе, и курсор в query запроса
 */
function dropPaginationFromUrl() {
  if (pagination.isPaginated.value) router.replace({ path: route.path })

  setFilter({ CursorField: undefined, CursorId: undefined })
}
const currentSort = ref({
  sortField: 'manual',
  sortDirection: SortDirection.ASC,
})
const sentinelRef = ref<HTMLElement | null>(null)
useIntersectionObserver(
  sentinelRef,
  ([entry]) => {
    if (entry?.isIntersecting && hasAfter.value && !pending.value) {
      nextPage()
    }
  },
  { threshold: 0.1 },
)

const items = computed(() => collection.value || [])

// Ecommerce: показ списка (impressions). Отправляем только новые позиции
// (стартовая SSR-выдача + догрузка при infinite-scroll/смене фильтра), с позицией
// в общем списке; list — категория/поиск/каталог
const ecommerce = useEcommerce()
const impressedProductIds = new Set<number>()
const impressionsList = computed(
  () => props.category?.name || (props.searchQuery ? 'Поиск' : 'Каталог'),
)

// immediate: стартовый список приезжает из SSR-пейлоада и на клиенте не «меняется»,
// поэтому обычный watch его пропускает — без immediate impressions первой выдачи теряются.
// На сервере колбэк — no-op (import.meta.server), счётчик к маунту уже инициализирован.
watch(items, (list) => {
  if (import.meta.server) return
  const fresh = list
    .map((product, index) => ({ product, position: index + 1 }))
    .filter(({ product }) => product.id != null && !impressedProductIds.has(product.id))
  if (!fresh.length) return

  fresh.forEach(({ product }) => impressedProductIds.add(product.id!))
  ecommerce.impressions(
    fresh.map(({ product, position }) =>
      mapCatalogProduct(product, { list: impressionsList.value, position }),
    ),
  )
}, { flush: 'post', immediate: true })

const outOfStockModel = computed({
  get() {
    return !query.value['Filter.OutOfStock']
  },
  set(value: boolean) {
    dropPaginationFromUrl()
    setFilter({
      'Filter.OutOfStock': !value,
    })
    load(true)
  },
})

watch(currentSort, (newSort) => {
  dropPaginationFromUrl()
  setFilter({
    SortField: newSort.sortField,
    SortDirection: newSort.sortDirection,
  })
  load(true)
}, { deep: true })

watch(
  () => props.category?.id,
  () => {
    dropPaginationFromUrl()
    setFilter({
      'Filter.CategoryId': props.category?.id,
      SortField: currentSort.value.sortField,
      SortDirection: currentSort.value.sortDirection,
    })
    load(true)
  },
)

watch(
  () => props.searchQuery,
  () => {
    setFilter({
      Query: props.searchQuery,
      SortField: currentSort.value.sortField,
      SortDirection: currentSort.value.sortDirection,
    })
    load(true)
  },
)
</script>
